import { useCallback, useEffect, useMemo, useState } from "react";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { Building2, Loader2, Plus, Search, Star, Trash2, Users2, Link2, Download,
         ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { estApi } from "./lib/api";
import CompanyDrawer from "./components/CompanyDrawer";
import DirectoryLinkPanel from "./components/DirectoryLinkPanel";
import DuplicatesPanel from "./components/DuplicatesPanel";
import NewCustomerBanner from "./components/NewCustomerBanner";
import { peekNewCustomerRequest } from "./lib/navigation";

const inputCls = "h-8 w-full rounded-md border border-slate-200 px-2 text-xs";

/** Column header that sorts — same interaction as the Dashboard tables. */
function SortableHeader({ label, sortKey, sortState, onSort }) {
  const current = sortState.key === sortKey ? sortState.dir : null;
  return (
    <button className="group flex items-center gap-1 uppercase tracking-wide"
            onClick={() => onSort(sortKey)} data-testid={`directory-sort-${sortKey}`}>
      <span>{label}</span>
      <span className={current ? "opacity-100" : "opacity-0 transition-opacity group-hover:opacity-100"}>
        {current === "asc" ? <ArrowUp className="h-3 w-3" />
          : current === "desc" ? <ArrowDown className="h-3 w-3" />
          : <ArrowUpDown className="h-3 w-3 text-slate-400" />}
      </span>
    </button>
  );
}

/** The master company directory: who they are to us, and who to talk to there. */
export default function DirectoryPage() {
  const [summary, setSummary] = useState(null);
  const [dupsOpen, setDupsOpen] = useState(false);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCompany, setOpenCompany] = useState(null);
  const [panel, setPanel] = useState(null); // "link" | "import"
  // Bid Board "+ customer → add new": open a blank GC drawer, then keep the saved company handy.
  const [customerReq, setCustomerReq] = useState(() => peekNewCustomerRequest());
  const [newCustomer, setNewCustomer] = useState(null);
  useEffect(() => { if (customerReq && !newCustomer) setOpenCompany({ company_type: "gc", types: ["gc"], company_class: "customer" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onDrawerSaved = (saved) => {
    load();
    if (customerReq && saved?.id && !newCustomer) {
      setNewCustomer(saved);
      setOpenCompany(saved);              // stay open so the contact can be added right away
      return;
    }
    setOpenCompany(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, ct] = await Promise.all([
        estApi.directorySummary(),
        estApi.getCompanies({ search, company_type: type, limit: 500 }),
        estApi.getContacts({ limit: 500 }),
      ]);
      setSummary(s);
      setCompanies(c.companies || []);
      setContacts(ct.contacts || []);
    } finally {
      setLoading(false);
    }
  }, [search, type]);

  useEffect(() => { load(); }, [load]);

  const byCompany = useMemo(() => {
    const map = {};
    contacts.forEach((c) => {
      if (!c.company_id) return;
      (map[c.company_id] = map[c.company_id] || []).push(c);
    });
    return map;
  }, [contacts]);

  const typeLabel = (key) =>
    (summary?.types || []).find((t) => t.key === key)?.label || key;

  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const sortedCompanies = useMemo(() => {
    const value = (c) => {
      switch (sort.key) {
        case "type": return (c.types || [c.company_type]).map(typeLabel).join(", ").toLowerCase();
        case "rating": return c.rating || "";
        case "where": return [c.city, c.state].filter(Boolean).join(", ").toLowerCase();
        case "contacts": return (byCompany[c.id] || []).length;
        case "procurement": {
          const people = byCompany[c.id] || [];
          const proc = people.find((p) => p.id === c.procurement_contact_id);
          return (proc?.display_name || c.procurement_email || "").toLowerCase();
        }
        default: return (c.name || "").toLowerCase();
      }
    };
    const rows = [...companies].sort((a, b) => {
      const av = value(a), bv = value(b);
      if (typeof av === "number" && typeof bv === "number") {
        return av === bv ? (a.name || "").localeCompare(b.name || "") : av - bv;
      }
      // Blanks always sit at the bottom, whichever way the column is pointing.
      const as = String(av), bs = String(bv);
      if (!as && bs) return 1;
      if (as && !bs) return -1;
      if (as === bs) return (a.name || "").localeCompare(b.name || "");
      return as.localeCompare(bs);
    });
    if (sort.dir === "asc") return rows;
    const blanks = rows.filter((c) => !String(value(c)));
    const filled = rows.filter((c) => String(value(c)));
    return [...filled.reverse(), ...blanks];
  }, [companies, sort, byCompany, summary]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page-body" data-testid="directory-page">
      <div className="space-y-5">
        {customerReq && (
          <NewCustomerBanner request={customerReq} company={newCustomer}
                             onDismiss={() => { setCustomerReq(null); setNewCustomer(null); }} />)}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Directory
          </h1>
          <p className="text-xs text-slate-500">
            One master list of every firm we work with — general contractors, architects, owners,
            manufacturers, suppliers and subcontractors — and the people to contact at each. Estimates, proposals and
            purchase orders all read from here.
          </p>
        </div>

        {/* Counts + cleanup entry points */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat icon={Building2} label="Companies" value={summary?.companies ?? "—"} />
          <Stat icon={Users2} label="Contacts" value={summary?.contacts ?? "—"} />
          <button onClick={() => setPanel("link")} data-testid="directory-open-link"
                  className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-left transition-colors hover:bg-amber-100">
            <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-amber-700">
              <Link2 className="h-3.5 w-3.5" /> Needs linking
            </span>
            <span className="text-lg font-semibold text-amber-800">
              {summary?.unlinked_estimates ?? "—"}
            </span>
            <span className="block text-[10px] text-amber-700">estimates on a text-only customer</span>
          </button>
          <button onClick={() => setPanel("import")} data-testid="directory-open-import"
                  className="rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:bg-slate-50">
            <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
              <Download className="h-3.5 w-3.5" /> From Sales
            </span>
            <span className="text-lg font-semibold text-slate-800">
              {summary?.unmatched_sales_names ?? "—"}
            </span>
            <span className="block text-[10px] text-slate-500">customer names not in the directory</span>
          </button>
        </div>

        {/* Search first, then the type filter — same order on every page */}
        <div className="flex flex-wrap items-center gap-2">
          <SearchBar value={search} onChange={setSearch}
                     placeholder="Search..." testId="directory-search" />
          <div className="w-px h-6 bg-slate-200" />
          <Segmented
            options={[{ key: "", label: "All", count: summary?.companies, testId: "directory-type-all" },
                      ...(summary?.types || []).map((t) => ({ key: t.key, label: t.label, count: t.count,
                                                              testId: `directory-type-${t.key}` }))]}
            value={type}
            onChange={setType}
          />
          <Button size="sm" className="text-xs" data-testid="directory-add-company"
                  onClick={() => setOpenCompany({})}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Company
          </Button>
          <Button size="sm" variant="ghost" className="text-xs" data-testid="directory-duplicates-btn" title="Possible duplicates · merge"
                  onClick={() => setDupsOpen(true)}>
            <Link2 className="mr-1 h-3.5 w-3.5" /> Duplicates
          </Button>
        </div>

        {/* Companies */}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">
                  <SortableHeader label="Company" sortKey="name" sortState={sort} onSort={onSort} />
                </th>
                <th className="px-3 py-2 text-left">
                  <SortableHeader label="Type" sortKey="type" sortState={sort} onSort={onSort} />
                </th>
                <th className="px-3 py-2 text-left">
                  <SortableHeader label="Rating" sortKey="rating" sortState={sort} onSort={onSort} />
                </th>
                <th className="px-3 py-2 text-left">
                  <SortableHeader label="Where" sortKey="where" sortState={sort} onSort={onSort} />
                </th>
                <th className="px-3 py-2 text-left">
                  <SortableHeader label="Contacts" sortKey="contacts" sortState={sort} onSort={onSort} />
                </th>
                <th className="px-3 py-2 text-left">
                  <SortableHeader label="Procurement contact" sortKey="procurement" sortState={sort} onSort={onSort} />
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-400" />
                </td></tr>
              )}
              {!loading && !companies.length && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400"
                        data-testid="directory-empty">
                  Nothing here yet. Add a company, or pull the names Sales already uses.
                </td></tr>
              )}
              {!loading && sortedCompanies.map((c) => {
                const people = byCompany[c.id] || [];
                const proc = people.find((p) => p.id === c.procurement_contact_id);
                return (
                  <tr key={c.id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                      onClick={() => setOpenCompany(c)} data-testid={`directory-row-${c.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        {(c.types || [c.company_type]).map((t) => (
                          <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                            {typeLabel(t)}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-3 py-2" data-testid={`directory-rating-${c.id}`}>
                      {c.rating ? (
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          c.rating === "A" ? "bg-emerald-100 text-emerald-700"
                          : c.rating === "B" ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"}`}>
                          {c.rating}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {people.length || "—"}
                      {people.some((p) => p.is_primary) && (
                        <Star className="ml-1 inline h-3 w-3 fill-amber-400 text-amber-400" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {proc ? `${proc.display_name}${proc.email ? ` · ${proc.email}` : ""}`
                            : c.procurement_email || "—"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button title="Deactivate"
                              data-testid={`directory-deactivate-${c.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await estApi.updateCompany(c.id, { active: false });
                                toast.success(`${c.name} deactivated`);
                                load();
                              }}
                              className="text-slate-300 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openCompany && (
        <CompanyDrawer company={openCompany} types={summary?.types || []}
                       classes={summary?.classes || []}
                       contacts={openCompany.id ? (byCompany[openCompany.id] || []) : []}
                       onClose={() => setOpenCompany(null)}
                       onSaved={onDrawerSaved}
                       footer={customerReq && (!openCompany.id || newCustomer?.id === openCompany.id) ? (
                         <NewCustomerBanner request={customerReq} company={newCustomer} compact
                                            onDismiss={() => { setCustomerReq(null); setNewCustomer(null); setOpenCompany(null); }} />) : null} />
      )}
      {dupsOpen && <DuplicatesPanel onClose={() => setDupsOpen(false)} onMerged={load} />}
      {panel && (
        <DirectoryLinkPanel mode={panel}
                            onClose={() => setPanel(null)}
                            onDone={() => { setPanel(null); load(); }} />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <span className="text-lg font-semibold text-slate-800">{value}</span>
    </div>
  );
}

function TypeChip({ active, onClick, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`rounded-full border px-3 py-1 text-[11px] transition-colors ${
              active ? "border-[#007BFF] bg-blue-50 text-[#007BFF] font-medium"
                     : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
      {label}{count !== undefined && count !== null ? ` · ${count}` : ""}
    </button>
  );
}
